import React, { useState } from 'react';
import { Flag, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Aligns with backend ALLOWED_REASONS in routes/moderation.py
const REASONS = [
  { id: 'harassment',     label: 'Harassment or bullying' },
  { id: 'spam',           label: 'Spam or advertising'    },
  { id: 'hate-speech',    label: 'Hate speech'            },
  { id: 'self-harm',      label: 'Self-harm concern'      },
  { id: 'misinformation', label: 'Misinformation'         },
  { id: 'off-topic',      label: 'Off-topic'              },
  { id: 'other',          label: 'Other'                  },
];

/**
 * ReportCommentModal — used by every comment surface (CommentsSection +
 * CircleCommentSheet). Opens a bottom-sheet form with preset reasons and
 * an optional detail field, then hits `POST /api/comments/{id}/report`.
 * We deliberately avoid claiming "we'll act on this" — copy says the report
 * is queued for a human, which matches reality.
 */
const ReportCommentModal = ({ open, commentId, onClose, onReported }) => {
  const [reason, setReason]     = useState('spam');
  const [detail, setDetail]     = useState('');
  const [busy,   setBusy]       = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (!commentId) return;
    setBusy(true);
    try {
      const t = localStorage.getItem('rbr_token');
      const res = await fetch(`${API_URL}/api/comments/${commentId}/report`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        body:    JSON.stringify({ reason, detail: detail.trim() || undefined }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(body.detail || 'Could not send the report');
        return;
      }
      if (body.duplicate) {
        toast('You\u2019ve already reported this comment', { description: 'Our team is on it.' });
      } else {
        toast.success('Report sent 🙏', { description: 'A moderator will review it soon.' });
      }
      setReason('spam');
      setDetail('');
      onReported?.();
      onClose?.();
    } catch {
      toast.error('Network error — please try again');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="report-comment-modal"
    >
      <div className="bg-[#FFFCF3] w-full max-w-[480px] rounded-t-3xl sm:rounded-3xl shadow-2xl border border-[#D6AD60]/30 animate-fade-up">
        <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Flag className="w-4 h-4 text-orange-500" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.24em] font-semibold text-orange-500">
                Report Comment
              </p>
              <p className="text-[11px] text-stone-500 mt-0.5">
                Send this to the moderation queue.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-800 hover:bg-stone-100"
            data-testid="report-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4">
          <fieldset className="mb-3">
            <legend className="text-[11px] uppercase tracking-wide font-semibold text-stone-700 mb-2">
              What&rsquo;s the issue?
            </legend>
            <div className="space-y-1.5">
              {REASONS.map((r) => (
                <label
                  key={r.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer border transition-colors ${
                    reason === r.id ? 'bg-[#F7F3EA] border-[#D6AD60]' : 'bg-white border-stone-200 hover:bg-stone-50'
                  }`}
                  data-testid={`report-reason-${r.id}`}
                >
                  <input
                    type="radio"
                    name="report-reason"
                    value={r.id}
                    checked={reason === r.id}
                    onChange={() => setReason(r.id)}
                    className="w-3.5 h-3.5 accent-[#D6AD60]"
                  />
                  <span className="text-[13px] text-stone-700">{r.label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <label className="text-[11px] uppercase tracking-wide font-semibold text-stone-700 block mb-1">
            Anything else? <span className="text-stone-400 normal-case font-normal">(optional)</span>
          </label>
          <textarea
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Add context if you want — moderators will see it."
            className="w-full px-3 py-2 rounded-lg text-[13px] leading-relaxed resize-none bg-white text-stone-800 placeholder:text-stone-400 border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#D6AD60]/50"
            data-testid="report-detail-input"
          />
          <p className="text-[10px] text-stone-400 mt-1">{detail.length}/500</p>
        </div>

        <div className="border-t border-stone-200 p-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-full text-[12px] font-medium text-stone-600 hover:bg-stone-100"
            data-testid="report-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="px-5 py-2 rounded-full text-[12px] font-semibold text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-60 inline-flex items-center gap-1.5"
            data-testid="report-submit-btn"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Flag className="w-3.5 h-3.5" />}
            Send report
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportCommentModal;
